// Job Board — Phase 1 (Intake & Viewer).
//
// Left nav: departments → postings within each department.
// Right pane: posting details + resumes tab (upload, list, download,
// status change, delete). LLM scoring / rubric editor / ad-generation
// / talent parking come in Phase 2+.
//
// Access: super admin + base_role=staff (backend enforces).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Briefcase, Plus, Loader2, Upload, Download, Trash2, X, Save,
  FileText, ChevronRight, Building2, CheckCircle2, Clock,
  Inbox, MapPin, Users, Sparkles, MailOpen, Archive, Mail,
  ListChecks, Wand2, MessageSquare, Send, RefreshCw, Pencil, XCircle, Eye, EyeOff,
  Columns3, GripVertical, ArrowUpDown, RotateCcw, Layers, ArrowRight,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { ResumeHealthWidget } from "@/components/ResumeHealthWidget";
import { CollapsibleWidgetShell } from "@/components/CollapsibleWidgetShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { RubricDialog } from "@/pages/jobboard/RubricDialog";
import { RejectionTemplateDialog } from "@/pages/jobboard/RejectionTemplateDialog";
import { AdCopyDialog } from "@/pages/jobboard/AdCopyDialog";
import { CommentsPanel } from "@/pages/jobboard/CommentsPanel";
import { fmt as fmtUtil, fmtSize as fmtSizeUtil } from "@/pages/jobs/utils";
import { ParkedPool } from "@/pages/jobs/ParkedPool";
import { DeptRow, EmptyRightPane } from "@/pages/jobs/DeptRow";
import { ResumesTable } from "@/pages/jobs/ResumesTable";
import { usePageBreadcrumb } from "@/context/BreadcrumbContext";

const fmt = fmtUtil;
const fmtSize = fmtSizeUtil;


// Iter 87b: Column registry for the resume table. Same pattern as DID
// Master — every user can hide/reorder/sort. `sortKey` returns the raw
// value used for comparison; `render` returns the JSX cell body.
const RESUME_COLS = [
  { key: "candidate",   label: "Candidate", sortable: true, alwaysOn: true,
    sortKey: (r) => (r.candidate_name || "").toLowerCase() },
  { key: "status",      label: "Status",    sortable: true,
    sortKey: (r) => r.status || "" },
  { key: "ai_score",    label: "AI Score",  sortable: true, align: "center",
    sortKey: (r) => (r.latest_score == null ? -1 : r.latest_score) },
  { key: "uc_rank",     label: "UC Rank",   sortable: true, align: "center",
    sortKey: (r) => (r.uc_rank == null ? -1 : r.uc_rank) },
  { key: "file",        label: "File",      sortable: true,
    sortKey: (r) => (r.filename || "").toLowerCase() },
  { key: "uploaded",    label: "Uploaded",  sortable: true,
    sortKey: (r) => r.created_at || "" },
];
const RESUME_COL_MAP = Object.fromEntries(RESUME_COLS.map((c) => [c.key, c]));
const RESUME_COL_KEYS = RESUME_COLS.map((c) => c.key);

export default function JobBoard() {
  const { user, has } = useAuth();
  // Iter 115e — read-only viewers hold job_board.view without .manage.
  const canManageJobs = has("job_board.edit");
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get("filter") || "";

  const [departments,       setDepartments]       = useState([]);
  const [selectedDeptKey,   setSelectedDeptKey]   = useState("");
  // Iter 114h — all-candidates AI scoring report.
  const [reportOpen,        setReportOpen]        = useState(false);
  const [report,            setReport]            = useState(null);
  const [reportBusy,        setReportBusy]        = useState(false);
  const [reportRejected,    setReportRejected]    = useState(false);
  const [reportMin,         setReportMin]         = useState("");        // min score filter
  const [reportSort,        setReportSort]        = useState("score_desc");
  const [reportJdOpen,      setReportJdOpen]      = useState(false);
  const [mailOpen,          setMailOpen]          = useState(false);
  const [mailTo,            setMailTo]            = useState("");
  const [mailMsg,           setMailMsg]           = useState("");
  const [mailBusy,          setMailBusy]          = useState(false);
  const [mailRemember,      setMailRemember]      = useState(true);
  // Iter 88k: Parked virtual department — cross-posting pool of parked
  // candidates. Own state so we can load /jobs/parked independent of the
  // posting-specific resume list.
  const [parkedResumes,     setParkedResumes]     = useState([]);
  const [parkedCount,       setParkedCount]       = useState(0);
  const [parkedLoading,     setParkedLoading]     = useState(false);
  const [parkedSearch,      setParkedSearch]      = useState("");
  const [parkedSelected,    setParkedSelected]    = useState(() => new Set());  // Iter 88l: bulk selection
  const [bulkTransfer,      setBulkTransfer]      = useState(false);
  const [parkedBulkBusy,    setParkedBulkBusy]    = useState(false);
  const [transferTarget,    setTransferTarget]    = useState(null);       // resume being transferred (single mode)
  const [transferPostingId, setTransferPostingId] = useState("");
  const [allOpenPostings,   setAllOpenPostings]   = useState([]);          // for transfer picker
  const isParkedMode = selectedDeptKey === "_parked";

  const loadParked = async () => {
    setParkedLoading(true);
    try {
      const { data } = await api.get("/jobs/parked",
        { params: parkedSearch ? { q: parkedSearch } : {} });
      setParkedResumes(data.items || []);
      setParkedCount(data.total || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load parked pool");
    } finally { setParkedLoading(false); }
  };
  useEffect(() => { if (isParkedMode) loadParked(); }, [isParkedMode, parkedSearch]);
  // Refresh parked-count badge on load.
  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/jobs/parked");
      setParkedCount(data.total || 0);
    } catch { /* ignore */ }
  })(); }, []);

  const openTransfer = async (r) => {
    setTransferTarget(r);
    setBulkTransfer(false);
    setTransferPostingId("");
    // Pull every open posting across all departments so the picker has options.
    try {
      const { data } = await api.get("/jobs", { params: { status: "open", limit: 200 } });
      setAllOpenPostings((data.items || []).filter((p) => p.posting_id !== r.posting_id));
    } catch { setAllOpenPostings([]); }
  };
  // Iter 88l: open the transfer dialog for every currently-selected parked
  // resume. Same picker UI, but the confirm handler iterates through the
  // selection set.
  const openBulkTransfer = async () => {
    if (parkedSelected.size === 0) return;
    setBulkTransfer(true);
    setTransferTarget(null);
    setTransferPostingId("");
    try {
      const { data } = await api.get("/jobs", { params: { status: "open", limit: 200 } });
      setAllOpenPostings(data.items || []);
    } catch { setAllOpenPostings([]); }
  };
  const doTransfer = async () => {
    if (!transferPostingId) return;
    if (bulkTransfer) {
      const ids = Array.from(parkedSelected);
      if (!ids.length) { setBulkTransfer(false); return; }
      const t = toast.loading(`Transferring ${ids.length} candidate${ids.length === 1 ? "" : "s"}…`);
      setParkedBulkBusy(true);
      let ok = 0, fail = 0;
      for (const id of ids) {
        try {
          await api.post(`/jobs/resumes/${id}/transfer`, { target_posting_id: transferPostingId });
          ok += 1;
        } catch { fail += 1; }
      }
      const target = allOpenPostings.find((p) => p.posting_id === transferPostingId);
      toast[fail === 0 ? "success" : "warning"](
        `Moved ${ok} → ${target?.title || "target"}${fail ? ` · ${fail} failed` : ""} (scores reset)`,
        { id: t, duration: 6000 });
      setBulkTransfer(false);
      setParkedSelected(new Set());
      setParkedBulkBusy(false);
      await loadParked();
      return;
    }
    if (!transferTarget) return;
    const t = toast.loading("Transferring…");
    try {
      await api.post(`/jobs/resumes/${transferTarget.resume_id}/transfer`,
        { target_posting_id: transferPostingId });
      const target = allOpenPostings.find((p) => p.posting_id === transferPostingId);
      toast.success(`Moved ${transferTarget.candidate_name || "candidate"} → ${target?.title || "target"} (score reset)`, { id: t, duration: 6000 });
      setTransferTarget(null);
      await loadParked();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transfer failed", { id: t });
    }
  };
  // Iter 88l: bulk restore — moves each selected parked resume back to
  // status="new" on its original posting.
  const doBulkRestore = async () => {
    const ids = Array.from(parkedSelected);
    if (!ids.length) return;
    const t = toast.loading(`Restoring ${ids.length} candidate${ids.length === 1 ? "" : "s"}…`);
    setParkedBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.patch(`/jobs/resumes/${id}/status`, { status: "new" });
        ok += 1;
      } catch { fail += 1; }
    }
    toast[fail === 0 ? "success" : "warning"](
      `Restored ${ok}${fail ? ` · ${fail} failed` : ""}`, { id: t });
    setParkedSelected(new Set());
    setParkedBulkBusy(false);
    await loadParked();
  };
  // Iter 88l: bulk delete — confirms in the existing AlertDialog and then
  // deletes each selected resume in sequence.
  const doBulkDelete = async () => {
    const ids = Array.from(parkedSelected);
    if (!ids.length) return;
    const t = toast.loading(`Deleting ${ids.length} candidate${ids.length === 1 ? "" : "s"}…`);
    setParkedBulkBusy(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.delete(`/jobs/resumes/${id}`);
        ok += 1;
      } catch { fail += 1; }
    }
    toast[fail === 0 ? "success" : "warning"](
      `Deleted ${ok}${fail ? ` · ${fail} failed` : ""}`, { id: t });
    setParkedSelected(new Set());
    setParkedBulkBusy(false);
    setConfirmDelete(null);
    await loadParked();
  };
  const [postings,          setPostings]          = useState([]);
  const [selectedPostingId, setSelectedPostingId] = useState("");
  const [resumes,           setResumes]           = useState([]);
  const [loadingDept,       setLoadingDept]       = useState(true);
  const [loadingResumes,    setLoadingResumes]    = useState(false);
  const [resumeFilter,      setResumeFilter]      = useState(initialFilter);

  const [postingDialogOpen, setPostingDialogOpen] = useState(false);
  const [postingDialogMode, setPostingDialogMode] = useState("create"); // create | edit
  const [postingForm,       setPostingForm]       = useState(emptyPostingForm());

  const [uploadDialogOpen,  setUploadDialogOpen]  = useState(false);
  const [uploadForm,        setUploadForm]        = useState({ candidate_name: "", candidate_email: "", candidate_phone: "" });
  const [uploadFile,        setUploadFile]        = useState(null);
  const [uploading,         setUploading]         = useState(false);
  // Iter 87: track upload-success + duplicate states so the button flips
  // to a green "Uploaded ✓" pill after a good POST and can't be re-clicked
  // (guards against double-uploads while the resume list is repopulating).
  const [uploadSuccess,     setUploadSuccess]     = useState(false);
  const [uploadDuplicate,   setUploadDuplicate]   = useState(null); // {message, duplicate_of}
  const fileInputRef = useRef(null);

  const [confirmDelete,     setConfirmDelete]     = useState(null);   // {kind: "posting"|"resume", id, label}
  // Iter 87: resume edit dialog + rejected-toggle + uc-rank inline state.
  const [editResume,        setEditResume]        = useState(null);   // resume doc
  const [editForm,          setEditForm]          = useState({ candidate_name: "", candidate_email: "", candidate_phone: "", uc_rank: "" });
  const [editSaving,        setEditSaving]        = useState(false);
  const [showRejected,      setShowRejected]      = useState(false);
  const [rejectedCount,     setRejectedCount]     = useState(0);
  const [rankBusy,          setRankBusy]          = useState({});      // resume_id → bool
  // Iter 88g: resume preview dialog — click the filename to open.
  const [previewResume,     setPreviewResume]     = useState(null);
  // Iter 88g-fix: Chrome blocks direct PDF iframes from same-origin
  // API paths ("This page has been blocked by Chrome"). Workaround:
  // fetch the PDF as a blob and set a blob: URL — Chrome always allows
  // those inside iframes.
  const [previewBlobUrl,    setPreviewBlobUrl]    = useState("");
  useEffect(() => {
    let revoked = null;
    (async () => {
      if (!previewResume) { setPreviewBlobUrl(""); return; }
      const fname = (previewResume.filename || "").toLowerCase();
      const ctype = previewResume.content_type || "";
      const isPdf = fname.endsWith(".pdf") || ctype.includes("pdf");
      if (!isPdf) { setPreviewBlobUrl(""); return; }
      try {
        const r = await api.get(`/jobs/resumes/${previewResume.resume_id}/preview`, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setPreviewBlobUrl(url);
      } catch (e) {
        setPreviewBlobUrl("");
        toast.error("Couldn't load preview");
      }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [previewResume]);

  // Iter 87b: per-user column prefs (order + hidden) + sort state,
  // persisted in localStorage. Matches DidMaster's pattern so users get
  // the same customization affordances across the app.
  const RESUME_PREF_KEY = `resume_cols_v1_${user?.user_id || "anon"}`;
  const [resumeColOrder,  setResumeColOrder]  = useState(RESUME_COL_KEYS);
  const [resumeColHidden, setResumeColHidden] = useState([]);
  const [resumeSort,      setResumeSort]      = useState({ key: "uploaded", dir: "desc" });
  const [resumeDragKey,   setResumeDragKey]   = useState(null);
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(RESUME_PREF_KEY) || "{}");
      const ord = Array.isArray(p.order) ? p.order.filter((k) => RESUME_COL_MAP[k]) : [];
      setResumeColOrder([...ord, ...RESUME_COL_KEYS.filter((k) => !ord.includes(k))]);
      setResumeColHidden(Array.isArray(p.hidden) ? p.hidden.filter((k) => RESUME_COL_MAP[k]) : []);
      if (p.sort?.key && RESUME_COL_MAP[p.sort.key]) {
        setResumeSort({ key: p.sort.key, dir: p.sort.dir === "asc" ? "asc" : "desc" });
      }
    } catch { /* defaults */ }
  }, [RESUME_PREF_KEY]);
  const persistResumePrefs = (order, hidden, sort) => {
    localStorage.setItem(RESUME_PREF_KEY, JSON.stringify({ order, hidden, sort }));
  };
  const toggleResumeCol = (key) => {
    setResumeColHidden((h) => {
      const nh = h.includes(key) ? h.filter((k) => k !== key) : [...h, key];
      persistResumePrefs(resumeColOrder, nh, resumeSort);
      return nh;
    });
  };
  const resetResumeCols = () => {
    setResumeColOrder(RESUME_COL_KEYS);
    setResumeColHidden([]);
    persistResumePrefs(RESUME_COL_KEYS, [], resumeSort);
  };
  const toggleResumeSort = (key) => {
    setResumeSort((s) => {
      const next = s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" };
      persistResumePrefs(resumeColOrder, resumeColHidden, next);
      return next;
    });
  };
  const onResumeColDrop = (dropKey) => {
    if (!resumeDragKey || resumeDragKey === dropKey) { setResumeDragKey(null); return; }
    setResumeColOrder((cur) => {
      const arr = cur.filter((k) => k !== resumeDragKey);
      const at = arr.indexOf(dropKey);
      arr.splice(at, 0, resumeDragKey);
      persistResumePrefs(arr, resumeColHidden, resumeSort);
      return arr;
    });
    setResumeDragKey(null);
  };
  const visibleResumeCols = useMemo(
    () => resumeColOrder.filter((k) => !resumeColHidden.includes(k)).map((k) => RESUME_COL_MAP[k]).filter(Boolean),
    [resumeColOrder, resumeColHidden]
  );

  // Iter 79b: dialogs + score data.
  const [rubricOpen,   setRubricOpen]   = useState(false);
  const [adCopyOpen,   setAdCopyOpen]   = useState(false);
  const [rejectTplOpen, setRejectTplOpen] = useState(false);
  const [scoreBusy,    setScoreBusy]    = useState(false);
  const [scoreDetails, setScoreDetails] = useState({}); // resume_id → score doc
  const [detailScore,  setDetailScore]  = useState(null); // popover payload
  const [openComments, setOpenComments] = useState({});   // resume_id → bool
  const [parkBusy,     setParkBusy]     = useState({});
  // Bulk-upload dropzone state
  const [bulkOpen,     setBulkOpen]     = useState(false);
  const [bulkFiles,    setBulkFiles]    = useState([]);
  const [bulkBusy,     setBulkBusy]     = useState(false);
  const [bulkResult,   setBulkResult]   = useState(null);
  const bulkInputRef   = useRef(null);
  // JD auto-draft state
  const [jdBusy, setJdBusy] = useState(false);

  // Iter 80c: collaborator picker — staff users list (loaded on-demand
  // when the edit posting dialog opens).
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [collabSearch, setCollabSearch] = useState("");

  const staffAllowed =
    user?.role === "super_admin" || user?.base_role === "staff";

  // -- Load departments on mount ---------------------------------------
  useEffect(() => {
    if (!staffAllowed) return;
    (async () => {
      try {
        setLoadingDept(true);
        const { data } = await api.get("/jobs/departments");
        setDepartments(data.items || []);
        if ((data.items || []).length && !selectedDeptKey) {
          setSelectedDeptKey(data.items[0].key);
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load departments");
      } finally { setLoadingDept(false); }
    })();
  }, [staffAllowed]);

  // -- Reload postings when dept changes -------------------------------
  const reloadPostings = async (deptKey) => {
    if (!deptKey) return;
    try {
      const { data } = await api.get("/jobs", { params: { department_key: deptKey } });
      setPostings(data.items || []);
      // Auto-select first open posting when nothing selected
      if (!selectedPostingId || !(data.items || []).some((p) => p.posting_id === selectedPostingId)) {
        const firstOpen = (data.items || []).find((p) => p.status === "open") || (data.items || [])[0];
        setSelectedPostingId(firstOpen?.posting_id || "");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load postings");
    }
  };
  useEffect(() => { reloadPostings(selectedDeptKey); }, [selectedDeptKey]);

  // -- Reload resumes when posting changes -----------------------------
  const [resumeSearch, setResumeSearch] = useState("");
  const [debouncedResumeSearch, setDebouncedResumeSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedResumeSearch(resumeSearch), 220);
    return () => clearTimeout(t);
  }, [resumeSearch]);

  const reloadResumes = async (postingId) => {
    if (!postingId) { setResumes([]); setRejectedCount(0); return; }
    try {
      setLoadingResumes(true);
      const params = { include_rejected: showRejected };
      if (debouncedResumeSearch) params.q = debouncedResumeSearch;
      const { data } = await api.get(`/jobs/${postingId}/resumes`, { params });
      setResumes(data.items || []);
      setRejectedCount(data.rejected_count || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load resumes");
    } finally { setLoadingResumes(false); }
  };
  useEffect(() => { reloadResumes(selectedPostingId); }, [selectedPostingId, showRejected, debouncedResumeSearch]);

  // -- Derived: currently selected posting ----------------------------
  const selectedPosting = useMemo(
    () => postings.find((p) => p.posting_id === selectedPostingId) || null,
    [postings, selectedPostingId],
  );

  usePageBreadcrumb(useMemo(() => (selectedPosting ? [
    { label: "Job Board", to: "/jobs" },
    { label: selectedPosting.title || "Posting" },
  ] : null), [selectedPosting]));

  // Apply the ?filter=... query from the Resume Health tile.
  const filteredResumes = useMemo(() => {
    if (!resumeFilter) return resumes;
    if (resumeFilter === "new" || resumeFilter === "awaiting") {
      return resumes.filter((r) => r.status === "new");
    }
    if (resumeFilter === "parked-stale") {
      const cutoff = Date.now() - 180 * 24 * 60 * 60 * 1000;
      return resumes.filter((r) => r.status === "parked"
        && r.parked_at && new Date(r.parked_at).getTime() <= cutoff);
    }
    return resumes;
  }, [resumes, resumeFilter]);

  // -- Posting dialog handlers -----------------------------------------
  const openCreatePosting = () => {
    setPostingDialogMode("create");
    setPostingForm({ ...emptyPostingForm(), department_key: selectedDeptKey || "" });
    setPostingDialogOpen(true);
  };
  const openEditPosting = () => {
    if (!selectedPosting) return;
    setPostingDialogMode("edit");
    setPostingForm({
      posting_id:       selectedPosting.posting_id,
      title:            selectedPosting.title || "",
      department_key:   selectedPosting.department_key || "",
      description:      selectedPosting.description || "",
      location:         selectedPosting.location || "",
      employment_type:  selectedPosting.employment_type || "full-time",
      status:           selectedPosting.status || "open",
      visible_user_ids: selectedPosting.visible_user_ids || [],
    });
    setCollabSearch("");
    if (!staffUsers.length && !staffLoading) {
      (async () => {
        try {
          setStaffLoading(true);
          const { data } = await api.get("/users", { params: { status: "active" } });
          const list = Array.isArray(data) ? data : (data.users || []);
          setStaffUsers(list.filter((u) => u.base_role === "staff" || u.role === "super_admin"));
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load staff");
        } finally { setStaffLoading(false); }
      })();
    }
    setPostingDialogOpen(true);
  };
  const savePosting = async () => {
    const payload = {
      title:           postingForm.title.trim(),
      department_key:  postingForm.department_key,
      description:     postingForm.description,
      location:        postingForm.location,
      employment_type: postingForm.employment_type,
      status:          postingForm.status,
    };
    if (postingDialogMode === "edit") {
      payload.visible_user_ids = postingForm.visible_user_ids || [];
    }
    if (!payload.title) { toast.error("Title is required"); return; }
    if (!payload.department_key) { toast.error("Pick a department"); return; }
    try {
      if (postingDialogMode === "create") {
        const { data } = await api.post("/jobs", payload);
        toast.success("Posting created");
        setPostingDialogOpen(false);
        // Move UI to the new dept + posting.
        if (data.department_key !== selectedDeptKey) setSelectedDeptKey(data.department_key);
        else await reloadPostings(selectedDeptKey);
        setSelectedPostingId(data.posting_id);
      } else {
        const { data } = await api.patch(`/jobs/${postingForm.posting_id}`, payload);
        toast.success("Posting updated");
        setPostingDialogOpen(false);
        if (data.department_key !== selectedDeptKey) setSelectedDeptKey(data.department_key);
        else await reloadPostings(selectedDeptKey);
        setSelectedPostingId(data.posting_id);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const doDeletePosting = async () => {
    if (!confirmDelete || confirmDelete.kind !== "posting") return;
    try {
      await api.delete(`/jobs/${confirmDelete.id}`);
      toast.success("Posting deleted");
      setConfirmDelete(null);
      setSelectedPostingId("");
      await reloadPostings(selectedDeptKey);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const doDeleteResume = async () => {
    if (!confirmDelete || confirmDelete.kind !== "resume") return;
    try {
      await api.delete(`/jobs/resumes/${confirmDelete.id}`);
      toast.success("Resume deleted");
      setConfirmDelete(null);
      // Iter 88l: parked mode has no selectedPostingId — refresh the
      // parked pool instead so the deleted row disappears immediately.
      if (isParkedMode) await loadParked();
      else               await reloadResumes(selectedPostingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  // -- Resume upload ---------------------------------------------------
  // Iter 88: dedup preview + apply.
  const [dedupeOpen,    setDedupeOpen]    = useState(false);
  const [dedupePreview, setDedupePreview] = useState(null);   // { groups, total_duplicates, ... }
  const [dedupeBusy,    setDedupeBusy]    = useState(false);
  const [dedupeAction,  setDedupeAction]  = useState("delete"); // "reject" | "delete"
  const [dedupeKeeps,   setDedupeKeeps]   = useState({});       // group_id → resume_id to keep

  const openDedupe = async () => {
    if (!selectedPostingId) { toast.error("Pick a posting first"); return; }
    setDedupeOpen(true);
    setDedupePreview(null);
    setDedupeKeeps({});
    try {
      setDedupeBusy(true);
      const { data } = await api.post(`/jobs/${selectedPostingId}/dedupe`,
        { dry_run: true });
      setDedupePreview(data);
      // Default: keep the canonical (oldest) of each group.
      const kmap = {};
      (data.groups || []).forEach((g) => { kmap[g.group_id] = g.canonical.resume_id; });
      setDedupeKeeps(kmap);
      if (!data.groups.length) {
        toast.success("No duplicates found");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Dedup scan failed");
    } finally { setDedupeBusy(false); }
  };
  const applyDedupe = async () => {
    if (!dedupePreview?.groups?.length) return;
    const t = toast.loading(`${dedupeAction === "delete" ? "Deleting" : "Rejecting"} duplicates…`);
    try {
      setDedupeBusy(true);
      const keep_resume_ids = Object.values(dedupeKeeps);
      const { data } = await api.post(`/jobs/${selectedPostingId}/dedupe`, {
        dry_run: false, action: dedupeAction, keep_resume_ids,
      });
      toast.success(`${dedupeAction === "delete" ? "Deleted" : "Rejected"} ${data.acted} duplicate resume${data.acted === 1 ? "" : "s"}`, { id: t });
      setDedupeOpen(false);
      await reloadResumes(selectedPostingId);
      await reloadPostings(selectedDeptKey);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Dedup failed", { id: t });
    } finally { setDedupeBusy(false); }
  };

  const openUpload = () => {
    if (!selectedPostingId) { toast.error("Pick a posting first"); return; }
    setUploadForm({ candidate_name: "", candidate_email: "", candidate_phone: "" });
    setUploadFile(null);
    setUploadSuccess(false);
    setUploadDuplicate(null);
    setUploadDialogOpen(true);
  };
  const submitUpload = async () => {
    if (!uploadFile) { toast.error("Choose a PDF or DOCX file"); return; }
    const fd = new FormData();
    fd.append("file", uploadFile);
    if (uploadForm.candidate_name)  fd.append("candidate_name",  uploadForm.candidate_name);
    if (uploadForm.candidate_email) fd.append("candidate_email", uploadForm.candidate_email);
    if (uploadForm.candidate_phone) fd.append("candidate_phone", uploadForm.candidate_phone);
    try {
      setUploading(true);
      setUploadDuplicate(null);
      await api.post(`/jobs/${selectedPostingId}/resumes`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Iter 87: flip the button to a green "Uploaded ✓" state so we can't
      // fire the same POST twice while the resume list is repopulating.
      setUploadSuccess(true);
      toast.success("Resume uploaded");
      await reloadResumes(selectedPostingId);
      // Refresh posting list to update resume_count badge
      await reloadPostings(selectedDeptKey);
      // Auto-close so the user can immediately upload the next one.
      setTimeout(() => { setUploadDialogOpen(false); }, 1200);
    } catch (e) {
      // Iter 87: dedup response — surface it inline instead of a toast so
      // the user can decide (change file / dismiss).
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === "object") {
        setUploadDuplicate(detail);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Upload failed");
      }
    } finally { setUploading(false); }
  };

  // Iter 87: reject / restore / edit / rank helpers.
  const rejectResume = async (r) => {
    try {
      await api.patch(`/jobs/resumes/${r.resume_id}/status`, { status: "rejected" });
      toast.success("Rejected — hidden from default view");
      await reloadResumes(selectedPostingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reject failed");
    }
  };
  const restoreResume = async (r) => {
    try {
      await api.patch(`/jobs/resumes/${r.resume_id}/status`, { status: "new" });
      toast.success("Restored to review pool");
      await reloadResumes(selectedPostingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };
  const openEditResume = (r) => {
    setEditForm({
      candidate_name:  r.candidate_name  || "",
      candidate_email: r.candidate_email || "",
      candidate_phone: r.candidate_phone || "",
      uc_rank:         (r.uc_rank ?? "") === null ? "" : String(r.uc_rank ?? ""),
    });
    setEditResume(r);
  };
  const saveEditResume = async () => {
    if (!editResume) return;
    try {
      setEditSaving(true);
      const body = {
        candidate_name:  editForm.candidate_name,
        candidate_email: editForm.candidate_email,
        candidate_phone: editForm.candidate_phone,
        uc_rank:         editForm.uc_rank === "" ? null : Number(editForm.uc_rank),
      };
      await api.patch(`/jobs/resumes/${editResume.resume_id}`, body);
      toast.success("Candidate updated");
      setEditResume(null);
      await reloadResumes(selectedPostingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setEditSaving(false); }
  };
  const setUCRank = async (resumeId, value) => {
    const num = value === "" ? null : Number(value);
    if (num !== null && Number.isNaN(num)) return;
    try {
      setRankBusy((b) => ({ ...b, [resumeId]: true }));
      await api.patch(`/jobs/resumes/${resumeId}`, { uc_rank: num });
      // Optimistic patch on the in-memory row so the input keeps focus.
      setResumes((rs) => rs.map((r) => r.resume_id === resumeId ? { ...r, uc_rank: num } : r));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rank save failed");
    } finally { setRankBusy((b) => ({ ...b, [resumeId]: false })); }
  };

  const setResumeStatus = async (resumeId, status) => {
    try {
      await api.patch(`/jobs/resumes/${resumeId}/status`, { status });
      toast.success(`Marked as ${status}`);
      await reloadResumes(selectedPostingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status update failed");
    }
  };

  // ---- Iter 79b actions ----------------------------------------------
  const scoreOne = async (resumeId, force = false) => {
    try {
      setScoreBusy(true);
      const { data } = await api.post(
        `/jobs/resumes/${resumeId}/score${force ? "?force=true" : ""}`);
      setScoreDetails((m) => ({ ...m, [resumeId]: data }));
      toast.success(`Scored ${data.score}/100`);
      await reloadResumes(selectedPostingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scoring failed");
    } finally { setScoreBusy(false); }
  };
  const scoreAll = async () => {
    if (!selectedPostingId) return;
    const pid = selectedPostingId;
    const t = toast.loading("Starting AI scoring…");
    try {
      setScoreBusy(true);
      const { data } = await api.post(`/jobs/${pid}/score-all`);
      if (data.total === 0) {
        toast.info("No resumes to score", { id: t });
        setScoreBusy(false);
        return;
      }
      // Iter 114i — scoring runs in the background; poll for progress so the
      // request can never be killed by the proxy mid-batch.
      const poll = async () => {
        try {
          const { data: s } = await api.get(`/jobs/${pid}/score-run`);
          const done = (s.scored || 0) + (s.skipped || 0) + (s.failed || 0);
          if (s.status === "running") {
            toast.loading(`Scoring candidates… ${done}/${s.total}`, { id: t });
            setTimeout(poll, 3000);
            return;
          }
          toast.success(`Scored ${s.scored} · cached ${s.skipped} · failed ${s.failed}`, { id: t });
          setScoreBusy(false);
          await reloadResumes(pid);
        } catch {
          toast.error("Lost track of the scoring run — refresh to see results", { id: t });
          setScoreBusy(false);
        }
      };
      setTimeout(poll, 2500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Batch scoring failed", { id: t });
      setScoreBusy(false);
    }
  };
  // Iter 114h — all-candidates AI scoring report (Name / Score / Reason).
  // Iter 114k — server-side min-score filter + sorting so the table, the
  // CSV/PDF exports and the emailed PDF always agree.
  const reportParams = (over = {}) => {
    const p = {
      include_rejected: over.includeRejected ?? reportRejected,
      sort: over.sort ?? reportSort,
    };
    const min = over.min ?? reportMin;
    if (min !== "" && min !== null && !Number.isNaN(Number(min))) p.min_score = Number(min);
    return p;
  };
  const openReport = async (over = {}) => {
    if (!selectedPostingId) return;
    setReportOpen(true);
    setReportBusy(true);
    try {
      const { data } = await api.get(`/jobs/${selectedPostingId}/candidate-report`,
        { params: reportParams(over) });
      setReport(data);
      // Iter 114n — prefill the saved hiring-manager email for this posting.
      if (data?.posting?.report_recipients) setMailTo(data.posting.report_recipients);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to build report");
      setReport(null);
    } finally { setReportBusy(false); }
  };
  const reportUrl = (ext) => {
    const qs = new URLSearchParams(Object.entries(reportParams()).map(([k, v]) => [k, String(v)]));
    return `${api.defaults.baseURL}/jobs/${selectedPostingId}/candidate-report.${ext}?${qs}`;
  };
  const emailReport = async () => {
    if (!mailTo.trim()) { toast.error("Enter at least one recipient email"); return; }
    setMailBusy(true);
    try {
      const p = reportParams();
      const { data } = await api.post(`/jobs/${selectedPostingId}/candidate-report/email`, {
        to: mailTo.trim(), message: mailMsg.trim(),
        include_rejected: p.include_rejected, sort: p.sort,
        min_score: p.min_score ?? null, remember: mailRemember,
      });
      toast.success(`Report emailed to ${data.recipients.join(", ")} (${data.candidates} candidates)`
        + (data.remembered ? " · saved for this posting" : ""));
      setMailOpen(false); setMailMsg("");
      setReport((r) => (r ? { ...r, posting: { ...r.posting, report_recipients: data.recipients.join(", ") } } : r));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to email report");
    } finally { setMailBusy(false); }
  };
  const openScoreDetail = async (resume) => {
    let doc = scoreDetails[resume.resume_id];
    if (!doc) {
      try {
        const { data } = await api.get(`/jobs/resumes/${resume.resume_id}/score`);
        doc = data;
        setScoreDetails((m) => ({ ...m, [resume.resume_id]: data }));
      } catch { doc = null; }
    }
    setDetailScore({ resume, score: doc });
  };
  const sendPark = async (resume, kind = "parking") => {
    try {
      setParkBusy((b) => ({ ...b, [resume.resume_id]: true }));
      const { data } = await api.post(
        `/jobs/resumes/${resume.resume_id}/park-email`, { kind });
      toast.success(data.sandbox
        ? "Sandbox log written (no email sent)"
        : data.delivered ? "Email sent" : "Email skipped");
      await reloadResumes(selectedPostingId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally { setParkBusy((b) => ({ ...b, [resume.resume_id]: false })); }
  };
  const openBulkUpload = () => {
    if (!selectedPostingId) { toast.error("Pick a posting first"); return; }
    setBulkFiles([]); setBulkResult(null); setBulkOpen(true);
  };
  const submitBulk = async () => {
    if (!bulkFiles.length) { toast.error("Pick some files first"); return; }
    const fd = new FormData();
    bulkFiles.forEach((f) => fd.append("files", f));
    try {
      setBulkBusy(true);
      const { data } = await api.post(
        `/jobs/${selectedPostingId}/resumes/bulk`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      // Iter 87: clear picked files so the user can queue a fresh batch
      // without deleting the old ones one-by-one (and can't re-submit the
      // same batch twice).
      setBulkFiles([]);
      toast.success(`Uploaded ${data.success_count} · ${data.fail_count} failed`);
      await reloadResumes(selectedPostingId);
      await reloadPostings(selectedDeptKey);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk upload failed");
    } finally { setBulkBusy(false); }
  };
  const draftJDWithClaude = async () => {
    if (!postingForm.title.trim()) { toast.error("Add a title first"); return; }
    try {
      setJdBusy(true);
      const { data } = await api.post("/jobs/draft-description", {
        title:           postingForm.title,
        department_key:  postingForm.department_key,
        location:        postingForm.location,
        employment_type: postingForm.employment_type,
        hints:           postingForm.description,   // reuse existing text as hint
      });
      setPostingForm({ ...postingForm, description: data.description || "" });
      toast.success("Drafted with Claude");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Draft failed");
    } finally { setJdBusy(false); }
  };

  const downloadResume = (r) => {
    const url = `${api.defaults.baseURL}/jobs/resumes/${r.resume_id}/download`;
    window.open(url, "_blank");
  };

  if (!staffAllowed) {
    return (
      <div className="p-8" data-testid="job-board-forbidden">
        <PageHeader title="Job Board" subtitle="Access restricted" />
        <Card className="p-6 mt-4">
          <p className="text-sm text-slate-600">
            The Job Board is available to super admins and staff only.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="job-board-page">
      <PageHeader
        title="Job Board"
        subtitle="Post roles, collect resumes, and (soon) rank candidates with AI."
      >
        {canManageJobs && (
        <Button onClick={openCreatePosting} data-testid="job-board-new-posting-btn"
                className="gap-2">
          <Plus className="h-4 w-4" /> New posting
        </Button>
        )}
      </PageHeader>

      {/* Iter 90c — Resume Health widget moved from Dashboard. Collapsible;
          preference persisted per user in localStorage. */}
      <div className="mt-4">
        <CollapsibleWidgetShell
          variant="bare"
          storageKey="jobboard_resume_health_open" defaultOpen
          title="Resume health"
          testid="collapsible-resume-health">
          <ResumeHealthWidget />
        </CollapsibleWidgetShell>
      </div>

      <div className="mt-6 grid grid-cols-12 gap-4">
        {/* Left sidebar: departments → postings */}
        <Card className="col-span-12 md:col-span-4 lg:col-span-3 p-0 overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Departments
            </div>
          </div>
          {loadingDept ? (
            <div className="p-6 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto"
                 data-testid="job-board-dept-list">
              {departments.map((d) => (
                <DeptRow key={d.key} dept={d}
                         selected={d.key === selectedDeptKey}
                         onClick={() => { setSelectedDeptKey(d.key); setSelectedPostingId(""); setParkedSelected(new Set()); }}
                         postings={d.key === selectedDeptKey ? postings : null}
                         selectedPostingId={selectedPostingId}
                         onPickPosting={setSelectedPostingId} />
              ))}
              {/* Iter 88k: Parked virtual department — yellow tint to
                  distinguish from real departments. */}
              <button onClick={() => { setSelectedDeptKey("_parked"); setSelectedPostingId(""); setParkedSelected(new Set()); }}
                      data-testid="dept-parked"
                      className={`w-full flex items-center gap-2 px-4 py-3 text-left transition border-l-4 ${
                        isParkedMode
                          ? "bg-amber-100 border-amber-400"
                          : "bg-amber-50/60 border-amber-200 hover:bg-amber-100"
                      }`}>
                <Archive className={`h-4 w-4 shrink-0 ${isParkedMode ? "text-amber-700" : "text-amber-600"}`} />
                <span className={`flex-1 text-sm font-medium ${isParkedMode ? "text-amber-900" : "text-amber-800"}`}>
                  Parked
                </span>
                <span className="rounded bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 tabular-nums"
                      data-testid="dept-parked-count">
                  {parkedCount}
                </span>
              </button>
            </div>
          )}
        </Card>

        {/* Right pane: posting detail + resumes */}
        <Card className="col-span-12 md:col-span-8 lg:col-span-9 p-0 overflow-hidden">
          {isParkedMode ? (
            <ParkedPool
              resumes={parkedResumes}
              loading={parkedLoading}
              search={parkedSearch}
              onSearch={setParkedSearch}
              onTransfer={openTransfer}
              onRestore={(r) => setResumeStatus(r.resume_id, "new").then(loadParked)}
              onDelete={(r) => setConfirmDelete({ kind: "resume",
                id: r.resume_id, label: r.candidate_name || r.filename })}
              selected={parkedSelected}
              onToggle={(id) => setParkedSelected((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
              })}
              onToggleAll={(checked) => setParkedSelected(
                checked ? new Set(parkedResumes.map((r) => r.resume_id)) : new Set())}
              onClearSelection={() => setParkedSelected(new Set())}
              onBulkTransfer={openBulkTransfer}
              onBulkRestore={doBulkRestore}
              onBulkDelete={() => setConfirmDelete({
                kind:  "resume-bulk",
                count: parkedSelected.size,
                label: `${parkedSelected.size} parked candidate${parkedSelected.size === 1 ? "" : "s"}`,
              })}
              bulkBusy={parkedBulkBusy}
            />
          ) : !selectedPosting ? (
            <EmptyRightPane onCreate={openCreatePosting} hasPostings={postings.length > 0} />
          ) : (
            <div className="flex flex-col">
              <PostingHeader posting={selectedPosting}
                             onEdit={openEditPosting}
                             onUpload={openUpload}
                             onBulkUpload={openBulkUpload}
                             onRubric={() => setRubricOpen(true)}
                             onAdCopy={() => setAdCopyOpen(true)}
                             onRejectTpl={() => setRejectTplOpen(true)}
                             onScoreAll={scoreAll}
                             onReport={() => openReport()}
                             scoreBusy={scoreBusy}
                             onDedupe={openDedupe}
                             onDelete={() => setConfirmDelete({ kind: "posting",
                               id: selectedPosting.posting_id, label: selectedPosting.title })} />
              <ResumesTable resumes={filteredResumes}
                            loading={loadingResumes}
                            filter={resumeFilter}
                            onClearFilter={() => setResumeFilter("")}
                            onSetStatus={setResumeStatus}
                            onDownload={downloadResume}
                            onScore={scoreOne}
                            onScoreDetail={openScoreDetail}
                            onSendPark={sendPark}
                            parkBusy={parkBusy}
                            openComments={openComments}
                            onToggleComments={(rid) => setOpenComments((m) => ({ ...m, [rid]: !m[rid] }))}
                            onDelete={(r) => setConfirmDelete({ kind: "resume",
                              id: r.resume_id, label: r.candidate_name || r.filename })}
                            /* Iter 87 additions */
                            onReject={rejectResume}
                            onRestore={restoreResume}
                            onEditResume={openEditResume}
                            onRankChange={setUCRank}
                            rankBusy={rankBusy}
                            showRejected={showRejected}
                            rejectedCount={rejectedCount}
                            onToggleShowRejected={() => setShowRejected((v) => !v)}
                            /* Iter 87b: column customization */
                            visibleCols={visibleResumeCols}
                            allCols={RESUME_COLS}
                            hiddenCols={resumeColHidden}
                            onToggleCol={toggleResumeCol}
                            onResetCols={resetResumeCols}
                            sort={resumeSort}
                            onToggleSort={toggleResumeSort}
                            dragKey={resumeDragKey}
                            onDragStart={setResumeDragKey}
                            onDrop={onResumeColDrop}
                            /* Iter 88g: click filename to preview */
                            onPreview={setPreviewResume}
                            /* Iter 87c: search */
                            search={resumeSearch}
                            onSearch={setResumeSearch} />
            </div>
          )}
        </Card>
      </div>

      {/* -------- Posting create / edit dialog -------- */}
      <Dialog open={postingDialogOpen} onOpenChange={setPostingDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="job-board-posting-dialog">
          <DialogHeader>
            <DialogTitle>{postingDialogMode === "create" ? "New posting" : "Edit posting"}</DialogTitle>
            <DialogDescription>
              Postings live under a department. Status &ldquo;open&rdquo; makes them visible in
              the active nav for anyone with Job Board access.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="posting-title">Title</Label>
              <Input id="posting-title" data-testid="posting-title-input"
                     value={postingForm.title}
                     onChange={(e) => setPostingForm({ ...postingForm, title: e.target.value })}
                     placeholder="e.g. Senior VoIP Engineer" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Department</Label>
                <Select value={postingForm.department_key}
                        onValueChange={(v) => setPostingForm({ ...postingForm, department_key: v })}>
                  <SelectTrigger data-testid="posting-dept-select"><SelectValue placeholder="Pick one" /></SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => (
                      <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Status</Label>
                <Select value={postingForm.status}
                        onValueChange={(v) => setPostingForm({ ...postingForm, status: v })}>
                  <SelectTrigger data-testid="posting-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="posting-loc">Location</Label>
                <Input id="posting-loc" data-testid="posting-location-input"
                       value={postingForm.location}
                       onChange={(e) => setPostingForm({ ...postingForm, location: e.target.value })}
                       placeholder="Calgary / Remote / Hybrid" />
              </div>
              <div className="grid gap-1.5">
                <Label>Employment type</Label>
                <Select value={postingForm.employment_type}
                        onValueChange={(v) => setPostingForm({ ...postingForm, employment_type: v })}>
                  <SelectTrigger data-testid="posting-emptype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="posting-desc">Description</Label>
              <Textarea id="posting-desc" data-testid="posting-description-input"
                        rows={5}
                        value={postingForm.description}
                        onChange={(e) => setPostingForm({ ...postingForm, description: e.target.value })}
                        placeholder="What is this role, who it's for, must-haves and nice-to-haves…" />
            </div>
            {postingDialogMode === "edit" && (
              <CollaboratorPicker
                selectedIds={postingForm.visible_user_ids || []}
                onChange={(ids) => setPostingForm({ ...postingForm, visible_user_ids: ids })}
                users={staffUsers}
                loading={staffLoading}
                search={collabSearch}
                onSearch={setCollabSearch}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostingDialogOpen(false)}
                    data-testid="posting-dialog-cancel">Cancel</Button>
            <Button onClick={savePosting} data-testid="posting-dialog-save" className="gap-2">
              <Save className="h-4 w-4" /> {postingDialogMode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Resume upload dialog -------- */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="job-board-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload resume</DialogTitle>
            <DialogDescription>
              PDF or DOCX up to 12 MB. We try to pull the candidate&rsquo;s name / email /
              phone automatically — override anything below if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Resume file</Label>
              <button onClick={() => fileInputRef.current?.click()}
                      data-testid="resume-file-picker"
                      className="w-full rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center hover:bg-slate-100 transition">
                {uploadFile ? (
                  <div className="text-sm text-slate-700">
                    <FileText className="mx-auto h-6 w-6 text-indigo-500 mb-1" />
                    <div className="font-medium truncate">{uploadFile.name}</div>
                    <div className="text-xs text-slate-500">{fmtSize(uploadFile.size)}</div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">
                    <Upload className="mx-auto h-6 w-6 mb-1" />
                    Click to pick a PDF or DOCX
                  </div>
                )}
              </button>
              <input ref={fileInputRef} type="file"
                     data-testid="resume-file-input"
                     accept=".pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                     className="hidden"
                     onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="cand-name">Candidate name</Label>
                <Input id="cand-name" data-testid="resume-cand-name"
                       value={uploadForm.candidate_name}
                       onChange={(e) => setUploadForm({ ...uploadForm, candidate_name: e.target.value })}
                       placeholder="(auto-detected if blank)" />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="cand-email">Email</Label>
                <Input id="cand-email" type="email" data-testid="resume-cand-email"
                       value={uploadForm.candidate_email}
                       onChange={(e) => setUploadForm({ ...uploadForm, candidate_email: e.target.value })}
                       placeholder="(auto-detected if blank)" />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="cand-phone">Phone</Label>
              <Input id="cand-phone" data-testid="resume-cand-phone"
                     value={uploadForm.candidate_phone}
                     onChange={(e) => setUploadForm({ ...uploadForm, candidate_phone: e.target.value })}
                     placeholder="(optional)" />
            </div>
          </div>
          {/* Iter 87: dedup response surfaced inline. */}
          {uploadDuplicate && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                 data-testid="upload-duplicate-banner">
              <div className="font-semibold">Already uploaded</div>
              <div className="mt-0.5">{uploadDuplicate.message}</div>
              {uploadDuplicate.duplicate_of && (
                <div className="mt-1 text-amber-800/80">
                  Existing: <b>{uploadDuplicate.duplicate_of.candidate_name || "unknown"}</b>
                  {uploadDuplicate.duplicate_of.candidate_email && <> · {uploadDuplicate.duplicate_of.candidate_email}</>}
                  {uploadDuplicate.duplicate_of.filename && <> · {uploadDuplicate.duplicate_of.filename}</>}
                </div>
              )}
              <button type="button" onClick={() => { setUploadDuplicate(null); setUploadFile(null); }}
                      data-testid="upload-duplicate-dismiss"
                      className="mt-1.5 rounded bg-white px-2 py-0.5 text-[11px] font-medium border border-amber-300 hover:bg-amber-100">
                Pick a different file
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}
                    disabled={uploading}
                    data-testid="upload-dialog-cancel">
              {uploadSuccess ? "Close" : "Cancel"}
            </Button>
            {uploadSuccess ? (
              <Button disabled data-testid="upload-dialog-success"
                      className="gap-2 bg-emerald-600 hover:bg-emerald-600 text-white cursor-default">
                <CheckCircle2 className="h-4 w-4" /> Uploaded — closing…
              </Button>
            ) : (
              <Button onClick={submitUpload} disabled={uploading || !uploadFile}
                      data-testid="upload-dialog-submit" className="gap-2">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Confirm delete -------- */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="job-board-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.kind === "posting"     ? "Delete posting?"
                : confirmDelete?.kind === "resume-bulk" ? `Delete ${confirmDelete?.count} parked candidate${confirmDelete?.count === 1 ? "" : "s"}?`
                : "Delete resume?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.kind === "posting"
                ? `"${confirmDelete?.label}" will be permanently removed. Postings with attached resumes cannot be deleted — close them instead.`
                : confirmDelete?.kind === "resume-bulk"
                  ? `${confirmDelete?.label} will be permanently removed, including their stored files. This cannot be undone.`
                  : `"${confirmDelete?.label}" will be permanently removed, including the stored file.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-ok"
              onClick={
                confirmDelete?.kind === "posting"      ? doDeletePosting :
                confirmDelete?.kind === "resume-bulk"  ? doBulkDelete :
                                                         doDeleteResume
              }>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 88k: Transfer parked candidate → any open posting.
          Iter 88l: also handles bulk transfer of every currently-selected
          parked candidate (bulkTransfer === true, transferTarget = null). */}
      <Dialog open={!!transferTarget || bulkTransfer}
              onOpenChange={(o) => { if (!o) { setTransferTarget(null); setBulkTransfer(false); } }}>
        <DialogContent className="max-w-md" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-emerald-600" /> Transfer candidate{bulkTransfer ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              {bulkTransfer ? (
                <>Move <b>{parkedSelected.size}</b> parked candidate{parkedSelected.size === 1 ? "" : "s"} into an open posting.
                Cached AI scores will be cleared — scores are role-specific and a fresh Score run will re-evaluate against the new posting&rsquo;s rubric.</>
              ) : (
                <>Move <b>{transferTarget?.candidate_name || "this candidate"}</b> into an open posting.
                The cached AI score will be cleared — scores are role-specific and a fresh Score run will re-evaluate against the new posting&rsquo;s rubric.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label>Target posting</Label>
            <Select value={transferPostingId} onValueChange={setTransferPostingId}>
              <SelectTrigger data-testid="transfer-target-select">
                <SelectValue placeholder="— pick an open posting —" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {allOpenPostings.map((p) => (
                  <SelectItem key={p.posting_id} value={p.posting_id}>
                    {p.title} <span className="text-slate-400 text-xs">· {p.department_key?.replace(/_/g,' ')}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!bulkTransfer && transferTarget?.parked_from_posting_title && (
              <div className="text-[11px] text-slate-500">
                Originally applied for: <b>{transferTarget.parked_from_posting_title}</b>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline"
                    onClick={() => { setTransferTarget(null); setBulkTransfer(false); }}
                    data-testid="transfer-cancel">Cancel</Button>
            <Button onClick={doTransfer} disabled={!transferPostingId || parkedBulkBusy}
                    data-testid="transfer-confirm"
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
              {parkedBulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {bulkTransfer ? `Transfer ${parkedSelected.size}` : "Transfer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 88g: Resume preview dialog — inline iframe render for PDFs
          (native browser viewer). DOCX / DOC / TXT fall back to a
          download nudge since browsers can't render them natively. */}
      <Dialog open={!!previewResume} onOpenChange={(o) => { if (!o) setPreviewResume(null); }}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0"
                       data-testid="resume-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 truncate">
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <span className="truncate">{previewResume?.filename}</span>
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2 text-[11px]">
              <span>{previewResume?.candidate_name || "— unknown —"}</span>
              {previewResume?.candidate_email && <><span>·</span><span className="text-slate-500">{previewResume.candidate_email}</span></>}
              {previewResume?.size_bytes ? <><span>·</span><span>{fmtSize(previewResume.size_bytes)}</span></> : null}
              <span className="ml-auto flex gap-1.5">
                <Button size="sm" variant="outline" className="h-6 gap-1 text-[11px]"
                        onClick={() => {
                          if (!previewResume) return;
                          window.open(
                            `${api.defaults.baseURL}/jobs/resumes/${previewResume.resume_id}/preview`,
                            "_blank", "noopener");
                        }}
                        data-testid="resume-preview-open-tab">
                  Open in new tab
                </Button>
                <Button size="sm" variant="outline" className="h-6 gap-1 text-[11px]"
                        onClick={() => previewResume && downloadResume(previewResume)}
                        data-testid="resume-preview-download">
                  <Download className="h-3 w-3" /> Download
                </Button>
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100">
            {previewResume && (() => {
              const ft = (previewResume.filename || "").toLowerCase();
              const isPdf = ft.endsWith(".pdf") || (previewResume.content_type || "").includes("pdf");
              const src = `${api.defaults.baseURL}/jobs/resumes/${previewResume.resume_id}/preview`;
              if (isPdf) {
                return previewBlobUrl ? (
                  <iframe title={`Preview ${previewResume.filename}`} src={previewBlobUrl}
                          data-testid="resume-preview-iframe"
                          className="h-full w-full border-0 bg-white" />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                  </div>
                );
              }
              return (
                <div className="flex h-full items-center justify-center p-8 text-center text-sm text-slate-500">
                  <div>
                    <FileText className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                    <div className="mb-1 font-medium text-slate-700">Browsers can&rsquo;t render {ft.split(".").pop().toUpperCase()} files inline.</div>
                    <div className="text-xs">The resume text has been extracted and is searchable. Use Download to open the original in Word or Preview.</div>
                    <Button size="sm" className="mt-3 gap-1.5"
                            onClick={() => downloadResume(previewResume)}
                            data-testid="resume-preview-fallback-download">
                      <Download className="h-3.5 w-3.5" /> Download {previewResume.filename}
                    </Button>
                    {(previewResume.text_full || previewResume.excerpt) && (
                      <details open className="mt-4 text-left rounded border border-slate-200 bg-white p-3">
                        <summary className="cursor-pointer text-xs font-medium text-slate-600">
                          Extracted resume text ({previewResume.text_chars || (previewResume.excerpt || "").length} chars)
                        </summary>
                        <pre className="mt-2 max-h-[55vh] overflow-y-auto whitespace-pre-wrap text-[11px] text-slate-700 leading-relaxed">
                          {previewResume.text_full || previewResume.excerpt}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter 88: Dedup preview + apply dialog. Groups duplicate resumes
          by content-hash / email / phone-digits, lets the user pick which
          copy to keep per group, and either rejects (safe, recoverable via
          Restore) or hard-deletes the rest. Backfills dedup keys on the
          fly so pre-Iter87 uploads participate. */}
      <Dialog open={dedupeOpen} onOpenChange={(o) => { if (!o) setDedupeOpen(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
                       data-testid="dedupe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-amber-600" />
              Find and remove duplicate resumes
            </DialogTitle>
            <DialogDescription>
              Groups candidates by identical file content, email, or phone
              number. Pick which copy to keep in each group — the rest will
              be rejected (recoverable) or deleted (permanent).
            </DialogDescription>
          </DialogHeader>
          {dedupeBusy && !dedupePreview && (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              Scanning for duplicates…
              <div className="text-[11px]">Backfilling content hashes on any older uploads.</div>
            </div>
          )}
          {dedupePreview && dedupePreview.groups?.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="dedupe-empty">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
              No duplicates found. Nothing to clean up.
              {dedupePreview.backfilled > 0 && (
                <div className="mt-1 text-[11px] text-slate-400">
                  ({dedupePreview.backfilled} historical uploads got dedup keys backfilled.)
                </div>
              )}
            </div>
          )}
          {dedupePreview && dedupePreview.groups?.length > 0 && (
            <>
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-900">
                  {dedupePreview.total_groups} group{dedupePreview.total_groups === 1 ? "" : "s"}
                </span>
                <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-900">
                  {dedupePreview.total_duplicates} duplicate{dedupePreview.total_duplicates === 1 ? "" : "s"} to remove
                </span>
                {dedupePreview.backfilled > 0 && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                    +{dedupePreview.backfilled} backfilled
                  </span>
                )}
                <span className="ml-auto flex items-center gap-1 text-slate-500">
                  Action:
                  <Select value={dedupeAction} onValueChange={setDedupeAction}>
                    <SelectTrigger className="h-7 w-32 text-xs" data-testid="dedupe-action-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reject">Reject (safe)</SelectItem>
                      <SelectItem value="delete">Delete (permanent)</SelectItem>
                    </SelectContent>
                  </Select>
                </span>
              </div>
              <div className="flex-1 overflow-y-auto rounded border border-slate-200"
                   data-testid="dedupe-groups">
                {dedupePreview.groups.map((g) => {
                  const all = [g.canonical, ...g.duplicates];
                  return (
                    <div key={g.group_id}
                         className="border-b border-slate-100 last:border-b-0 p-3"
                         data-testid={`dedupe-group-${g.group_id}`}>
                      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">
                          Group of {all.length}
                        </span>
                        {g.reasons.map((r, i) => (
                          <span key={i} className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-800 border border-amber-200">{r}</span>
                        ))}
                      </div>
                      <div className="space-y-1">
                        {all.map((r) => {
                          const keep = dedupeKeeps[g.group_id] === r.resume_id;
                          return (
                            <label key={r.resume_id}
                                   className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs cursor-pointer transition ${keep ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                                   data-testid={`dedupe-row-${r.resume_id}`}>
                              <input type="radio"
                                     name={`keep-${g.group_id}`}
                                     checked={keep}
                                     onChange={() => setDedupeKeeps((m) => ({ ...m, [g.group_id]: r.resume_id }))}
                                     data-testid={`dedupe-keep-${r.resume_id}`} />
                              <span className="w-14 text-[10px] font-semibold uppercase">
                                {keep ? <span className="text-emerald-700">Keep</span> : <span className="text-rose-600">{dedupeAction === "delete" ? "Delete" : "Reject"}</span>}
                              </span>
                              <span className="w-40 truncate font-medium">{r.candidate_name || "— unknown —"}</span>
                              <span className="w-48 truncate text-slate-500">{r.candidate_email || "—"}</span>
                              <span className="w-32 truncate text-slate-500">{r.candidate_phone || "—"}</span>
                              <span className="ml-auto text-[10px] text-slate-400">{fmt(r.created_at)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={dedupeBusy} onClick={() => setDedupeOpen(false)}
                    data-testid="dedupe-cancel">Cancel</Button>
            {dedupePreview?.groups?.length > 0 && (
              <Button onClick={applyDedupe} disabled={dedupeBusy}
                      data-testid="dedupe-apply"
                      className={`gap-2 ${dedupeAction === "delete" ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}`}>
                {dedupeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (dedupeAction === "delete" ? <Trash2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />)}
                {dedupeAction === "delete" ? "Delete duplicates" : "Reject duplicates"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 87d: AI Score breakdown modal — opens on click of the AI
          Score pill. Shows overall + per-criterion subscores + strengths
          + gaps + summary, plus the model + timestamp for auditability. */}
      <Dialog open={!!detailScore} onOpenChange={(o) => { if (!o) setDetailScore(null); }}>
        <DialogContent className="max-w-2xl" data-testid="score-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              AI Score — {detailScore?.resume?.candidate_name || "Candidate"}
            </DialogTitle>
            <DialogDescription>
              How Claude scored this resume against the posting rubric.
            </DialogDescription>
          </DialogHeader>
          {detailScore?.score ? (
            <div className="grid gap-4 text-sm">
              {/* Overall + meta */}
              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-4xl font-bold text-primary" data-testid="score-detail-overall">
                  {detailScore.score.score}
                  <span className="text-lg font-normal text-slate-400">/100</span>
                </div>
                <div className="text-xs text-slate-500">
                  <div>Model: <b>{detailScore.score.model || "claude"}</b></div>
                  <div>Scored: {fmt(detailScore.score.created_at)}</div>
                  {detailScore.score.rubric_hash && (
                    <div className="font-mono text-[10px] opacity-70">rubric hash: {detailScore.score.rubric_hash.slice(0, 12)}…</div>
                  )}
                </div>
              </div>
              {/* Summary */}
              {detailScore.score.summary && (
                <div>
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Summary</div>
                  <p className="text-slate-700">{detailScore.score.summary}</p>
                </div>
              )}
              {/* Per-criterion bars */}
              {detailScore.score.subscores && Object.keys(detailScore.score.subscores).length > 0 && (
                <div>
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Per-criterion breakdown
                  </div>
                  <div className="space-y-1.5" data-testid="score-detail-subscores">
                    {Object.entries(detailScore.score.subscores).map(([name, val]) => (
                      <div key={name} className="grid grid-cols-[1fr_50px_120px] items-center gap-2">
                        <span className="text-xs text-slate-700 truncate" title={name}>{name}</span>
                        <span className="text-xs font-semibold text-slate-800 text-right">{val}/100</span>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={`h-full ${val >= 80 ? "bg-emerald-500" : val >= 60 ? "bg-sky-500" : val >= 40 ? "bg-amber-500" : "bg-rose-500"}`}
                            style={{ width: `${Math.max(2, Math.min(100, val))}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Strengths / gaps */}
              <div className="grid gap-3 sm:grid-cols-2">
                {detailScore.score.strengths?.length > 0 && (
                  <div className="rounded-md border border-emerald-100 bg-emerald-50 p-2.5">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Strengths</div>
                    <ul className="list-disc pl-4 text-xs text-emerald-900 space-y-0.5">
                      {detailScore.score.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {detailScore.score.gaps?.length > 0 && (
                  <div className="rounded-md border border-rose-100 bg-rose-50 p-2.5">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-rose-700">Gaps</div>
                    <ul className="list-disc pl-4 text-xs text-rose-900 space-y-0.5">
                      {detailScore.score.gaps.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 text-center text-sm text-slate-500">
              No score cached yet. Run &ldquo;Score&rdquo; on the row to generate one.
            </div>
          )}
          <DialogFooter className="flex flex-wrap gap-2">
            {detailScore?.resume && (
              <>
                {/* Iter 88i: quick actions on the modal footer so a reviewer
                    doesn't have to close the dialog to rank / park / delete
                    / email a candidate. */}
                <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                  <span className="text-slate-500">UC Rank:</span>
                  <input type="number" inputMode="numeric"
                         defaultValue={detailScore.resume.uc_rank ?? ""}
                         onBlur={(e) => {
                           const v = e.target.value;
                           const cur = detailScore.resume.uc_rank ?? "";
                           if (String(v) !== String(cur)) setUCRank(detailScore.resume.resume_id, v);
                         }}
                         onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                         data-testid="score-detail-uc-rank"
                         placeholder="—"
                         className="w-16 h-6 rounded border border-slate-200 bg-white px-2 text-xs text-center focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30" />
                </div>
                <Button variant="outline" size="sm"
                        onClick={() => {
                          const r = detailScore.resume;
                          const subject = window.prompt("Subject", `Following up on your application`);
                          if (!subject) return;
                          const body = window.prompt("Email body (HTML allowed)",
                            `Hi ${r.candidate_name?.split(' ')[0] || 'there'},\n\nThanks for applying. We'd love to move to the next step — are you free for a short call this week?\n\nBest,\nCareers Team\ncareers@iplogin.io`);
                          if (!body) return;
                          const html = body.replace(/\n/g, "<br/>");
                          api.post(`/jobs/resumes/${r.resume_id}/careers-email`, { subject, body_html: html })
                            .then((res) => {
                              toast.success(res.data.sandbox
                                ? `Email logged (sandbox — no delivery)`
                                : `Email sent to ${res.data.to}`, { duration: 5000 });
                            })
                            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Email failed"));
                        }}
                        data-testid="score-detail-email">
                  <Mail className="h-3.5 w-3.5" /> Email
                </Button>
                <Button variant="outline" size="sm"
                        onClick={() => { setResumeStatus(detailScore.resume.resume_id, "parked"); setDetailScore(null); }}
                        data-testid="score-detail-park"
                        className="text-slate-700 border-slate-300 hover:bg-slate-50">
                  <Archive className="h-3.5 w-3.5" /> Park
                </Button>
                <Button variant="outline" size="sm"
                        onClick={() => {
                          if (window.confirm(`Permanently delete ${detailScore.resume.candidate_name || detailScore.resume.filename}?`)) {
                            api.delete(`/jobs/resumes/${detailScore.resume.resume_id}`)
                              .then(() => { toast.success("Deleted"); setDetailScore(null); reloadResumes(selectedPostingId); })
                              .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"));
                          }
                        }}
                        data-testid="score-detail-delete"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
                <Button variant="outline"
                        onClick={() => { scoreOne(detailScore.resume.resume_id, true); setDetailScore(null); }}
                        disabled={scoreBusy}
                        data-testid="score-detail-rerun">
                  {scoreBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Re-score
                </Button>
              </>
            )}
            <Button onClick={() => setDetailScore(null)} data-testid="score-detail-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 87: Resume edit dialog — correct sniffed candidate fields
          + set a freeform UC Rank that sits alongside the AI score. */}
      <Dialog open={!!editResume} onOpenChange={(o) => { if (!o) setEditResume(null); }}>
        <DialogContent className="max-w-lg" data-testid="resume-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit candidate</DialogTitle>
            <DialogDescription>
              Fix mis-sniffed contact fields and set a UC Rank. UC Rank is
              a freeform recruiter number — the higher, the better.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="edit-name">Candidate name</Label>
              <Input id="edit-name" data-testid="resume-edit-name"
                     value={editForm.candidate_name}
                     onChange={(e) => setEditForm({ ...editForm, candidate_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" data-testid="resume-edit-email"
                       value={editForm.candidate_email}
                       onChange={(e) => setEditForm({ ...editForm, candidate_email: e.target.value })} />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input id="edit-phone" data-testid="resume-edit-phone"
                       value={editForm.candidate_phone}
                       onChange={(e) => setEditForm({ ...editForm, candidate_phone: e.target.value })} />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="edit-rank">UC Rank <span className="text-slate-400">(number, blank to clear)</span></Label>
              <Input id="edit-rank" type="number" inputMode="numeric"
                     data-testid="resume-edit-rank"
                     value={editForm.uc_rank}
                     onChange={(e) => setEditForm({ ...editForm, uc_rank: e.target.value })}
                     placeholder="e.g. 85" />
            </div>
            {editResume?.filename && (
              <div className="text-[11px] text-slate-500">
                File on record: <span className="font-mono">{editResume.filename}</span> ({fmtSize(editResume.size_bytes)})
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={editSaving} onClick={() => setEditResume(null)}
                    data-testid="resume-edit-cancel">Cancel</Button>
            <Button onClick={saveEditResume} disabled={editSaving}
                    data-testid="resume-edit-save" className="gap-2">
              {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 114h — Candidate AI scoring report */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="flex max-h-[92vh] max-w-6xl flex-col overflow-hidden" data-testid="score-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-600" />
              Candidate AI scoring report
            </DialogTitle>
          </DialogHeader>
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-100 pb-3 text-xs text-slate-500">
            <span data-testid="score-report-meta">
              {report ? `${report.total} candidate${report.total === 1 ? "" : "s"} · ${report.scored_count} scored${report.avg_score != null ? ` · avg ${report.avg_score}` : ""}` : "—"}
            </span>
            <label className="flex items-center gap-2">
              <Checkbox checked={reportRejected}
                        onCheckedChange={(v) => { setReportRejected(!!v); openReport({ includeRejected: !!v }); }}
                        data-testid="score-report-include-rejected" />
              Include rejected
            </label>
            {/* Iter 114k — min-score filter + sort for instant shortlisting */}
            <label className="flex items-center gap-1.5">
              Min score
              <Input type="number" min="0" max="100" value={reportMin} placeholder="any"
                     data-testid="score-report-min"
                     onChange={(e) => setReportMin(e.target.value)}
                     onBlur={() => openReport()}
                     onKeyDown={(e) => { if (e.key === "Enter") openReport(); }}
                     className="h-8 w-20" />
            </label>
            <Select value={reportSort} onValueChange={(v) => { setReportSort(v); openReport({ sort: v }); }}>
              <SelectTrigger className="h-8 w-[168px]" data-testid="score-report-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="score_desc">Score: high → low</SelectItem>
                <SelectItem value="score_asc">Score: low → high</SelectItem>
                <SelectItem value="name_asc">Name: A → Z</SelectItem>
                <SelectItem value="recent">Newest upload first</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" className="gap-1.5"
                      data-testid="score-report-email"
                      onClick={() => setMailOpen(true)}>
                <Mail className="h-3.5 w-3.5" /> Email
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5"
                      data-testid="score-report-csv"
                      onClick={() => window.open(reportUrl("csv"), "_blank", "noopener")}>
                <Download className="h-3.5 w-3.5" /> CSV
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5"
                      data-testid="score-report-pdf"
                      onClick={() => window.open(reportUrl("pdf"), "_blank", "noopener")}>
                <Download className="h-3.5 w-3.5" /> PDF
              </Button>
            </div>
          </div>
          {/* Iter 114p — one scroll area for rubric + JD + table so the toolbar
              (Email / CSV / PDF) always stays pinned at the top. */}
          <div className="min-h-0 flex-1 overflow-auto pr-1">
          {/* Iter 114l — rubric summary + job description used for scoring */}
          {report?.rubric?.criteria?.length > 0 && (
            <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3"
                 data-testid="score-report-rubric">
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                <Sparkles className="h-3.5 w-3.5" /> Scoring rubric
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {report.rubric.criteria.map((c, i) => (
                  <div key={i} className="rounded-md border border-indigo-100 bg-white/70 p-2"
                       data-testid={`score-report-criterion-${i}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800">{c.name}</span>
                      <span className="shrink-0 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700">
                        {c.weight_pct}%
                      </span>
                    </div>
                    {c.description && <p className="mt-0.5 text-xs leading-snug text-slate-500">{c.description}</p>}
                  </div>
                ))}
              </div>
              {report.rubric.notes && (
                <p className="mt-2 text-xs text-slate-600"><span className="font-medium">Extra notes to the model:</span> {report.rubric.notes}</p>
              )}
              <button type="button" onClick={() => setReportJdOpen((v) => !v)}
                      data-testid="score-report-jd-toggle"
                      className="mt-2 flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline">
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${reportJdOpen ? "rotate-90" : ""}`} />
                {reportJdOpen ? "Hide" : "Show"} job description used
              </button>
              {reportJdOpen && (
                <div className="mt-2 max-h-56 overflow-auto rounded-md border border-indigo-100 bg-white p-3 text-xs"
                     data-testid="score-report-jd">
                  <JdBlocks blocks={report.posting?.description_blocks}
                            text={report.posting?.description} />
                </div>
              )}
            </div>
          )}
          <div className="mt-2 rounded-lg border border-slate-200">
            {reportBusy ? (
              <div className="flex items-center justify-center gap-2 p-10 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Building report…
              </div>
            ) : !report?.rows?.length ? (
              <div className="p-10 text-center text-sm text-slate-400" data-testid="score-report-empty">
                No candidates for this posting yet.
              </div>
            ) : (
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="border-b border-border bg-card px-3 py-2 font-medium text-muted-foreground">Name</th>
                    <th className="w-20 border-b border-border bg-card px-3 py-2 text-center font-medium text-muted-foreground">Score</th>
                    <th className="border-b border-border bg-card px-3 py-2 font-medium text-muted-foreground">Reason for Score</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.resume_id} className="align-top hover:bg-muted/60"
                        data-testid={`score-report-row-${r.resume_id}`}>
                      <td className="border-b border-border px-3 py-2.5">
                        <div className="font-medium text-foreground">{r.name}</div>
                        {r.email && <div className="text-xs text-muted-foreground">{r.email}</div>}
                        <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">{r.status}</div>
                      </td>
                      <td className="border-b border-border px-3 py-2.5 text-center">
                        {r.score == null ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <span className="inline-flex min-w-9 justify-center rounded-md bg-indigo-50 px-2 py-1 text-sm font-bold text-indigo-700"
                                data-testid={`score-report-score-${r.resume_id}`}>{r.score}</span>
                        )}
                      </td>
                      <td className="border-b border-border px-3 py-2.5">
                        {r.summary && <div className="text-sm text-foreground">{r.summary}</div>}
                        {!r.summary && r.score == null && (
                          <div className="text-sm text-muted-foreground">Not scored yet — run “Score all”.</div>
                        )}
                        {!!r.strengths?.length && (
                          <ul className="mt-1.5 space-y-0.5">
                            {r.strengths.slice(0, 3).map((p, i) => (
                              <li key={i} className="text-xs text-emerald-700">+ {p}</li>
                            ))}
                          </ul>
                        )}
                        {!!r.gaps?.length && (
                          <ul className="mt-1 space-y-0.5">
                            {r.gaps.slice(0, 3).map((g, i) => (
                              <li key={i} className="text-xs text-rose-700">− {g}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </div>
          <DialogFooter className="shrink-0 border-t border-slate-100 pt-3">
            <Button variant="outline" className="gap-2" data-testid="score-report-download-pdf"
                    onClick={() => window.open(reportUrl("pdf"), "_blank", "noopener")}>
              <Download className="h-4 w-4" /> Download PDF
            </Button>
            <Button variant="outline" className="gap-2" data-testid="score-report-download-csv"
                    onClick={() => window.open(reportUrl("csv"), "_blank", "noopener")}>
              <Download className="h-4 w-4" /> Download CSV
            </Button>
            <Button onClick={() => setReportOpen(false)} data-testid="score-report-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 114k — email the report PDF to a hiring manager */}
      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-lg" data-testid="score-report-mail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-indigo-600" /> Email scoring report
            </DialogTitle>
            <DialogDescription>
              Sends the PDF (rubric, job description and {report?.total ?? 0} candidate
              {report?.total === 1 ? "" : "s"} with current filters) as an attachment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>To</Label>
              <Input value={mailTo} onChange={(e) => setMailTo(e.target.value)}
                     placeholder="manager@iplogin.io, second@iplogin.io"
                     data-testid="score-report-mail-to" />
              {report?.posting?.report_recipients && (
                <p className="mt-1 text-xs text-emerald-700" data-testid="score-report-mail-preset">
                  Saved for this posting — sent last to {report.posting.report_recipients}
                </p>
              )}
            </div>
            <div>
              <Label>Message (optional)</Label>
              <Textarea rows={4} value={mailMsg} onChange={(e) => setMailMsg(e.target.value)}
                        placeholder="Here's the shortlist for the Tier 2/3 role…"
                        data-testid="score-report-mail-message" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Checkbox checked={mailRemember} onCheckedChange={(v) => setMailRemember(!!v)}
                        data-testid="score-report-mail-remember" />
              Remember these recipients for this posting
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMailOpen(false)} disabled={mailBusy}
                    data-testid="score-report-mail-cancel">Cancel</Button>
            <Button onClick={emailReport} disabled={mailBusy} className="gap-2"
                    data-testid="score-report-mail-send">
              {mailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Send report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mount Job Board dialogs — click handlers on PostingHeader flip
          the state flags below; without these renders they were no-ops. */}
      <RubricDialog
        open={rubricOpen}
        onOpenChange={setRubricOpen}
        posting={selectedPosting}
        onSaved={() => reloadPostings(selectedDeptKey)}
      />
      <AdCopyDialog
        open={adCopyOpen}
        onOpenChange={setAdCopyOpen}
        posting={selectedPosting}
      />
      <RejectionTemplateDialog
        open={rejectTplOpen}
        onOpenChange={setRejectTplOpen}
        posting={selectedPosting}
        onSaved={() => reloadPostings(selectedDeptKey)}
      />

      {/* Iter 84: Bulk resume upload dialog — was wired but never rendered.
          The "Bulk" button flips `bulkOpen`; this dialog handles the file
          picker + POST to /jobs/{posting_id}/resumes/bulk. */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkFiles([]); setBulkResult(null); } }}>
        <DialogContent className="max-w-xl" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk upload resumes</DialogTitle>
            <DialogDescription>
              Drop up to 40 resumes (PDF or Word). We&rsquo;ll parse the candidate&rsquo;s
              name, email and phone from each file — no manual entry needed.
            </DialogDescription>
          </DialogHeader>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const list = Array.from(e.dataTransfer.files || []);
              if (list.length) setBulkFiles((f) => [...f, ...list].slice(0, 40));
            }}
            data-testid="bulk-upload-dropzone"
            className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/60 p-6 text-center"
          >
            <Upload className="mx-auto h-6 w-6 text-primary" />
            <div className="mt-2 text-sm font-medium text-slate-800">
              Drop resumes here or click to choose
            </div>
            <div className="text-xs text-slate-500">PDF or Word · up to 40 files</div>
            <input ref={bulkInputRef} type="file" multiple
                   accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                   className="hidden" data-testid="bulk-upload-file-input"
                   onChange={(e) => {
                     const list = Array.from(e.target.files || []);
                     if (list.length) setBulkFiles((f) => [...f, ...list].slice(0, 40));
                     e.target.value = "";
                   }} />
            <Button size="sm" className="mt-3" onClick={() => bulkInputRef.current?.click()}
                    data-testid="bulk-upload-choose-btn">
              Choose files
            </Button>
          </div>
          {bulkFiles.length > 0 && (
            <div className="max-h-40 space-y-1 overflow-auto rounded-md border border-slate-200 p-2 text-xs"
                 data-testid="bulk-upload-file-list">
              {bulkFiles.map((f, i) => (
                <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-2">
                  <span className="truncate text-slate-800">{f.name}</span>
                  <button type="button" onClick={() => setBulkFiles((fs) => fs.filter((_, j) => j !== i))}
                          className="text-slate-400 hover:text-rose-600"
                          data-testid={`bulk-upload-remove-${i}`}
                          aria-label={`Remove ${f.name}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {bulkResult && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs"
                 data-testid="bulk-upload-result">
              <div className="font-medium text-slate-800">
                {bulkResult.success_count} uploaded · {bulkResult.fail_count} failed
              </div>
              {bulkResult.failed?.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-rose-600">
                  {bulkResult.failed.slice(0, 5).map((f, i) => (
                    <li key={i}>{f.filename}: {f.detail}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}
                    data-testid="bulk-upload-cancel">Close</Button>
            {bulkResult && bulkResult.success_count > 0 && !bulkBusy ? (
              <Button disabled data-testid="bulk-upload-success"
                      className="gap-2 bg-emerald-600 hover:bg-emerald-600 text-white cursor-default">
                <CheckCircle2 className="h-4 w-4" /> Uploaded {bulkResult.success_count} — pick more or close
              </Button>
            ) : (
              <Button onClick={submitBulk} disabled={bulkBusy || !bulkFiles.length}
                      data-testid="bulk-upload-submit">
                {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {bulkBusy ? "Uploading…" : `Upload ${bulkFiles.length} file${bulkFiles.length === 1 ? "" : "s"}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------

function emptyPostingForm() {
  return {
    posting_id:       "",
    title:            "",
    department_key:   "",
    description:      "",
    location:         "",
    employment_type:  "full-time",
    status:           "open",
    visible_user_ids: [],
  };
}

// Iter 88o: ParkedPool + DeptRow + EmptyRightPane were extracted to
// @/pages/jobs/ParkedPool and @/pages/jobs/DeptRow.

function PostingHeader({ posting, onEdit, onUpload, onBulkUpload, onRubric, onAdCopy, onRejectTpl, onScoreAll, scoreBusy, onDedupe, onDelete, onReport }) {
  const [jdOpen, setJdOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 px-6 py-4"
         data-testid="job-board-posting-header">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-[260px] flex-1">
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-lg font-semibold text-slate-900 truncate"
                data-testid="posting-title">
              {posting.title}
            </h2>
            {posting.status === "open" ? (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-medium"
                    data-testid="posting-status-badge">
                <CheckCircle2 className="h-3 w-3" /> Open
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 text-[10px] font-medium"
                    data-testid="posting-status-badge">
                <Clock className="h-3 w-3" /> Closed
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{posting.department_name}</span>
            {posting.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{posting.location}</span>}
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{posting.resume_count || 0} resume{(posting.resume_count || 0) === 1 ? "" : "s"}</span>
            <span className="text-slate-400">· {posting.employment_type}</span>
            <span className="text-slate-400">· Created {fmt(posting.created_at)}</span>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2">
          <Button onClick={onUpload} data-testid="posting-upload-resume-btn" className="gap-2">
            <Upload className="h-4 w-4" /> Upload resume
          </Button>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onBulkUpload}
                    data-testid="posting-bulk-upload-btn" className="gap-1.5">
              <Upload className="h-3.5 w-3.5" /> Bulk
            </Button>
            <Button variant="outline" size="sm" onClick={onRubric}
                    data-testid="posting-rubric-btn" className="gap-1.5">
              <ListChecks className="h-3.5 w-3.5" /> Rubric
            </Button>
            <Button variant="outline" size="sm" onClick={onRejectTpl}
                    data-testid="posting-reject-tpl-btn"
                    className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50">
              <MailOpen className="h-3.5 w-3.5" /> Reject email
              {posting.rejection_email_template && (
                <span className="ml-0.5 rounded-full bg-rose-100 px-1 py-0.5 text-[9px] font-bold uppercase text-rose-700"
                      data-testid="posting-reject-tpl-custom-dot"
                      title="This posting has a custom rejection email">
                  Custom
                </span>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={onScoreAll}
                    disabled={scoreBusy}
                    data-testid="posting-score-all-btn"
                    className="gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
              {scoreBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Score all
            </Button>
            <Button variant="outline" size="sm" onClick={onAdCopy}
                    data-testid="posting-adcopy-btn" className="gap-1.5">
              <Wand2 className="h-3.5 w-3.5" /> Ad copy
            </Button>
            {/* Iter 114h: all-candidates AI scoring report (table + exports). */}
            <Button variant="outline" size="sm" onClick={onReport}
                    data-testid="posting-report-btn"
                    className="gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50">
              <FileText className="h-3.5 w-3.5" /> Score report
            </Button>
            {/* Iter 88h: historical dedup — pre-Iter87 uploads have no guard,
                so this scans + rejects the older double-uploads. */}
            <Button variant="outline" size="sm" onClick={onDedupe}
                    data-testid="posting-dedupe-btn"
                    className="gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50">
              <Layers className="h-3.5 w-3.5" /> Find duplicates
            </Button>
            {/* Iter 88h: Top 5 candidate PDF export — rubric + methodology
                + per-candidate scoring/strengths/gaps/resume overview. */}
            <Button variant="outline" size="sm"
                    onClick={() => {
                      window.open(
                        `${api.defaults.baseURL}/jobs/${posting.posting_id}/top-5.pdf`,
                        "_blank", "noopener");
                    }}
                    data-testid="posting-top5-btn"
                    className="gap-1.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
              <Download className="h-3.5 w-3.5" /> Top 5 PDF
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}
                    data-testid="posting-edit-btn" className="flex-1">
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={onDelete}
                    data-testid="posting-delete-btn"
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      {/* Iter 114o — full-width, properly formatted job description. The old
          version was squeezed into a narrow column beside the action buttons
          and truncated at 96px, so the posting read as "incomplete". */}
      {posting.description && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3"
             data-testid="posting-description">
          <button type="button" onClick={() => setJdOpen((v) => !v)}
                  data-testid="posting-description-toggle"
                  className="flex w-full items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:text-slate-900">
            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${jdOpen ? "rotate-90" : ""}`} />
            Job description
            <span className="ml-auto text-[11px] font-normal normal-case text-slate-400">
              {jdOpen ? "Collapse" : "Expand"}
            </span>
          </button>
          <div className={`mt-2 overflow-y-auto pr-1 ${jdOpen ? "max-h-[420px]" : "max-h-32"}`}
               data-testid="posting-description-body">
            <JdBlocks blocks={posting.description_blocks} text={posting.description} />
          </div>
        </div>
      )}
    </div>
  );
}

// Iter 114o — shared renderer for a parsed job description (headings, bullets,
// paragraphs). Falls back to raw text when the backend sent no blocks.
function JdBlocks({ blocks, text }) {
  if (!blocks?.length) {
    return <p className="text-sm whitespace-pre-wrap text-slate-700">{text || "No job description saved."}</p>;
  }
  return (
    <div className="space-y-1.5 text-sm text-slate-700">
      {blocks.map((b, i) =>
        b.type === "heading" ? (
          <p key={i} className="pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-800">{b.text}</p>
        ) : b.type === "bullet" ? (
          <div key={i} className="flex gap-2 pl-1">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
            <span>{b.text}</span>
          </div>
        ) : (
          <p key={i} className="leading-relaxed">{b.text}</p>
        ))}
    </div>
  );
}

function CollaboratorPicker({ selectedIds, onChange, users, loading, search, onSearch }) {
  const selected = new Set(selectedIds || []);
  const toggle = (uid) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    onChange(Array.from(next));
  };
  const q = (search || "").trim().toLowerCase();
  const shown = users.filter((u) => {
    if (!q) return true;
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });
  const selectedUsers = users.filter((u) => selected.has(u.user_id));

  return (
    <div className="grid gap-1.5" data-testid="collaborator-picker">
      <div className="flex items-baseline justify-between">
        <Label>Collaborators (extra viewers)</Label>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {selected.size} selected · HR + super admin always see all postings
        </span>
      </div>
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="collaborator-chips">
          {selectedUsers.map((u) => (
            <button key={u.user_id} type="button" onClick={() => toggle(u.user_id)}
                    data-testid={`collaborator-chip-${u.user_id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200">
              {u.name || u.email}
              <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
      <Input
        placeholder="Search staff by name or email…"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        data-testid="collaborator-search"
      />
      <div className="rounded-md border border-slate-200 bg-white max-h-40 overflow-y-auto text-xs dark:border-slate-700 dark:bg-slate-900"
           data-testid="collaborator-list">
        {loading ? (
          <div className="p-3 text-slate-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading staff…
          </div>
        ) : shown.length === 0 ? (
          <div className="p-3 text-slate-500">No staff match &ldquo;{search}&rdquo;.</div>
        ) : (
          shown.slice(0, 40).map((u) => {
            const on = selected.has(u.user_id);
            return (
              <button key={u.user_id} type="button" onClick={() => toggle(u.user_id)}
                      data-testid={`collaborator-option-${u.user_id}`}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 ${on ? "bg-indigo-50/60 dark:bg-indigo-950/40" : ""}`}>
                <span className={`h-3.5 w-3.5 rounded border ${on ? "bg-indigo-600 border-indigo-600" : "border-slate-300"} flex items-center justify-center`}>
                  {on && <CheckCircle2 className="h-3 w-3 text-white" />}
                </span>
                <span className="flex-1 truncate text-slate-800 dark:text-slate-100">{u.name}</span>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{u.email}</span>
                <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400">{u.role_label || u.role}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

